import { computed, ref } from "vue";
import type { Component, VNode } from "vue";

const TOAST_LIMIT = 5;
const TOAST_REMOVE_DELAY = 1000; // 1 second after dismissal animation
const DEFAULT_TOAST_DURATION = 5000; // 5 seconds default duration

export type ToastVariant =
  | "default"
  | "destructive"
  | "success"
  | "warning"
  | "info"
  | "error";

export interface ToastAction {
  label: string;
  action: () => void;
  altText: string;
}

export interface Toast {
  id: string;
  title?: string;
  description?: string | VNode | Component;
  action?: ToastAction;
  variant?: ToastVariant;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export interface ToasterToast extends Toast {
  id: string;
  title?: string;
  description?: string | VNode | Component;
  action?: ToastAction;
}

export interface ToastState {
  toasts: ToasterToast[];
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | {
      type: ActionType["ADD_TOAST"];
      toast: ToasterToast;
    }
  | {
      type: ActionType["UPDATE_TOAST"];
      toast: Partial<ToasterToast>;
    }
  | {
      type: ActionType["DISMISS_TOAST"];
      toastId?: ToasterToast["id"];
    }
  | {
      type: ActionType["REMOVE_TOAST"];
      toastId?: ToasterToast["id"];
    };

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

function addToRemoveQueue(toastId: string) {
  if (toastTimeouts.has(toastId)) return;

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: actionTypes.REMOVE_TOAST,
      toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
}

const state = ref<ToastState>({
  toasts: [],
});

function dispatch(action: Action) {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      state.value.toasts = [action.toast, ...state.value.toasts].slice(
        0,
        TOAST_LIMIT,
      );
      break;

    case actionTypes.UPDATE_TOAST:
      state.value.toasts = state.value.toasts.map((t) =>
        t.id === action.toast.id ? { ...t, ...action.toast } : t,
      );
      break;

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action;

      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.value.toasts.forEach((t) => {
          addToRemoveQueue(t.id);
        });
      }

      state.value.toasts = state.value.toasts.map((t) =>
        t.id === toastId || toastId === undefined
          ? {
              ...t,
              open: false,
            }
          : t,
      );
      break;
    }

    case actionTypes.REMOVE_TOAST:
      if (action.toastId === undefined) {
        state.value.toasts = [];
      } else {
        state.value.toasts = state.value.toasts.filter(
          (t) => t.id !== action.toastId,
        );
      }

      break;
  }
}

function useToast() {
  return {
    toasts: computed(() => state.value.toasts),
    toast,
    dismiss: (toastId?: string) =>
      dispatch({ type: actionTypes.DISMISS_TOAST, toastId }),
  };
}

function toast(props: Omit<ToasterToast, "id"> & { duration?: number }) {
  const id = genId();
  const duration = props.duration ?? DEFAULT_TOAST_DURATION;

  const update = (props: ToasterToast) =>
    dispatch({
      type: actionTypes.UPDATE_TOAST,
      toast: { ...props, id },
    });

  const dismiss = () =>
    dispatch({ type: actionTypes.DISMISS_TOAST, toastId: id });

  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open: boolean) => {
        if (!open) dismiss();
      },
    },
  });

  // Set up auto-dismiss timer if duration is not 0
  if (duration > 0) {
    setTimeout(() => {
      dismiss();
    }, duration);
  }

  return {
    id,
    dismiss,
    update,
  };
}

function success(title: string, description?: string) {
  return toast({
    title,
    description,
    variant: "success",
  });
}

function error(title: string, description?: string) {
  return toast({
    title,
    description,
    variant: "error",
  });
}

function warning(title: string, description?: string) {
  return toast({
    title,
    description,
    variant: "warning",
  });
}

function info(title: string, description?: string) {
  return toast({
    title,
    description,
    variant: "info",
  });
}

export { toast, useToast, success, error, warning, info };
